import { useEffect, useRef, useCallback } from 'react'
import {
  COMMAND_PRIORITY_EDITOR,
  $getRoot,
  $getNodeByKey,
  DRAGOVER_COMMAND,
  DROP_COMMAND,
  PASTE_COMMAND,
  $createTextNode,
  $createRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  $nodesOfType
} from 'lexical'
import { mergeRegister } from '@lexical/utils'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { useFeeButton } from '@/components/fee-button'
import { FileUpload } from '@/components/file-upload'
import useDebounceCallback from '@/components/use-debounce-callback'
import { piconerosToSats, piconerosToMXmr } from '@/lib/format'
import { AWS_S3_URL_REGEXP } from '@/lib/constants'
import { getDragSelection } from '@/lib/lexical/utils/dom'
import styles from '@/lib/lexical/theme/editor.module.css'
import { $insertTextAtSelection } from '@/lib/lexical/utils'
import { isMarkdownMode } from '@/lib/lexical/commands/utils'
import { SN_UPLOAD_FILES_COMMAND } from '@/lib/lexical/commands/upload'
import { $createMediaNode, MediaNode } from '@/lib/lexical/nodes/content/media'
import { isAbortError } from '@/lib/error'

// submit disabled reason for upload
export const UPLOAD_SUBMIT_DISABLED_REASON = 'upload'

// The command itself now lives in lib/lexical/commands/upload.js (repo
// convention) so the addendum upload blocker can consume it without this
// module's fee/FileUpload graph. Re-exported here for existing importers.
export { SN_UPLOAD_FILES_COMMAND } from '@/lib/lexical/commands/upload'

const UPLOAD_FEES_QUERY = gql`
  query uploadFees($s3Keys: [Int]!) {
    uploadFees(s3Keys: $s3Keys) {
      totalFeesPiconeros
    }
  }
`

/**
 * plugin that handles file uploads and fee calculations
 * @returns {JSX.Element} hidden file upload input component
 */
export default function FileUploadPlugin ({ editorRef }) {
  const [editor] = useLexicalComposerContext()
  const placeholderKey = useRef(0)
  const fileInputRef = useRef(null)
  const { setDisabled: setSubmitDisabled } = useFeeButton()
  const { $refreshUploadFees } = useLexicalUploadFees(editor)

  // replace placeholder text in editor
  const $replacePlaceholder = useCallback((newContent) => {
    const placeholderNode = $getNodeByKey(placeholderKey.current)
    if (!placeholderNode) return
    // if newContent is a string, set the text content of the placeholder node
    // otherwise, replace the placeholder node with the new content
    if (typeof newContent === 'string') {
      placeholderNode.setTextContent(newContent)
      // move selection to end of the replaced node to avoid stale offset errors
      // (the old selection offset may exceed the new text length)
      placeholderNode.selectEnd()
    } else {
      placeholderNode.replace(newContent)
      $getRoot().selectEnd()
    }
  }, [editor, placeholderKey])

  // upload event handler
  // creates a placeholder node with the file name
  // inserts it into the editor at the selection or the root if there is no selection
  const onUpload = useCallback((file) => {
    editor.update(() => {
      const isMarkdown = isMarkdownMode(editor)
      // placeholderKey is the nodekey of the TextNode that contains the placeholder text
      const placeholderNode = $createTextNode(`![Uploading ${file.name}…]()`)
      $insertTextAtSelection(placeholderNode, isMarkdown ? 2 : 1)
      // update the placeholder key
      placeholderKey.current = placeholderNode.getKey()
    }, { tag: 'history-merge' })

    setSubmitDisabled?.(UPLOAD_SUBMIT_DISABLED_REASON, true)
  }, [editor, setSubmitDisabled])

  // success event handler
  // replaces the placeholder with the image url
  const onSuccess = useCallback(({ url, file }) => {
    const kind = file.type.split('/')[0]
    editor.update(() => {
      const isMarkdown = isMarkdownMode(editor)
      if (isMarkdown) {
        $replacePlaceholder(`![](${url})`)
      } else {
        const mediaNode = $createMediaNode({ src: url, kind, status: 'done' })
        $replacePlaceholder(mediaNode)
      }
    }, { tag: 'history-merge' })

    // refresh upload fees
    editor.read(() => $refreshUploadFees())
    setSubmitDisabled?.(UPLOAD_SUBMIT_DISABLED_REASON, false)
  }, [editor, $replacePlaceholder, $refreshUploadFees, setSubmitDisabled])

  const onError = useCallback(({ file }) => {
    editor.update(() => {
      $replacePlaceholder('')
    }, { tag: 'history-merge' })
    setSubmitDisabled?.(UPLOAD_SUBMIT_DISABLED_REASON, false)
  }, [editor, $replacePlaceholder, setSubmitDisabled])

  // drop event handler
  // sets lexical selection to the drag selection
  // and dispatches a change event to the file input
  const $onDrop = useCallback((e) => {
    e.preventDefault()
    if (editorRef) {
      editorRef.classList.remove(styles.dragOver)
    }

    // set lexical selection to the drag selection
    const range = getDragSelection(e)
    const rangeSelection = $createRangeSelection()
    if (range) {
      rangeSelection.applyDOMRange(range)
    }
    $setSelection(rangeSelection)

    const changeEvent = new Event('change', { bubbles: true })
    fileInputRef.current.files = e.dataTransfer.files
    fileInputRef.current.dispatchEvent(changeEvent)
  }, [editorRef])

  // command that opens the file upload dialog
  useEffect(() => {
    return editor.registerCommand(
      SN_UPLOAD_FILES_COMMAND,
      () => {
        if (!editor.isEditable()) return false

        fileInputRef.current?.click()
        return true
      },
      COMMAND_PRIORITY_EDITOR
    )
  }, [editor])

  // command listeners
  // paste, dragover, drop
  useEffect(() => {
    const unregisters = mergeRegister(
      editor.registerCommand(
        PASTE_COMMAND,
        (e) => {
          const items = e.clipboardData?.items || []
          if (items.length === 0) return false

          // rich text editors like Microsoft Word put a rendered bitmap of the selection on the
          // clipboard alongside the real text/html content. when there is actual text
          // to paste, prefer it and let the default rich-text/markdown paste handle it
          // instead of uploading the incidental image.
          const plain = e.clipboardData?.getData('text/plain')?.trim()
          const html = e.clipboardData?.getData('text/html')?.trim()
          // check for both, so that image uploads that carry e.g. a text/plain URL still upload
          if (plain && html) return false

          const fileList = new window.DataTransfer()
          let hasImages = false

          for (let i = 0; i < items.length; i++) {
            const item = items[i]
            const type = item.type.split('/')[0]
            if (type === 'image' || type === 'video') {
              const blob = item.getAsFile()
              if (!blob) continue
              const file = new File([blob], type, { type: blob.type })
              fileList.items.add(file)
              hasImages = true
            }
          }

          if (hasImages) {
            e.preventDefault()
            const changeEvent = new Event('change', { bubbles: true })
            fileInputRef.current.files = fileList.files
            fileInputRef.current.dispatchEvent(changeEvent)
          }

          return hasImages
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        DRAGOVER_COMMAND,
        (e) => {
          if (editorRef) {
            editorRef.classList.add(styles.dragOver)
          }
          return true
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        DROP_COMMAND,
        (e) => {
          $onDrop(e)
          return true
        },
        COMMAND_PRIORITY_HIGH
      )
    )

    const onDragLeave = () => {
      if (editorRef) {
        editorRef.classList.remove(styles.dragOver)
      }
    }

    if (editorRef) {
      editorRef.addEventListener('dragleave', onDragLeave)
    }
    return () => {
      unregisters()
      if (editorRef) {
        editorRef.removeEventListener('dragleave', onDragLeave)
      }
    }
  }, [editor, editorRef])

  // cleanup
  useEffect(() => {
    return () => {
      placeholderKey.current = 0
      // reset submit disabled reason
      setSubmitDisabled?.(UPLOAD_SUBMIT_DISABLED_REASON, false)
    }
  }, [placeholderKey, setSubmitDisabled])

  return (
    <div className='d-none'>
      <FileUpload
        multiple
        ref={fileInputRef}
        onUpload={onUpload}
        onSuccess={onSuccess}
        onError={onError}
      />
    </div>
  )
}

/**
 * hook for upload fees calculations
 *
 * updates the upload fees in the fee button when the editor state changes
 *
 * @param {Object} editor - lexical editor instance
 * @returns {Object} instant $refreshUploadFees function
 *
 * @example
 * ```javascript
 * const { $refreshUploadFees } = useLexicalUploadFees(editor)
 * $refreshUploadFees()
 * ```
 */
function useLexicalUploadFees (editor) {
  const { merge } = useFeeButton()

  const [updateUploadFees] = useLazyQuery(UPLOAD_FEES_QUERY, {
    fetchPolicy: 'no-cache'
  })

  const handleUploadFeesData = useCallback(({ data }) => {
    const { totalFeesPiconeros } = data.uploadFees
    const totalFees = piconerosToSats(totalFeesPiconeros)
    merge({
      uploadFees: {
        term: `+ ${piconerosToMXmr(BigInt(totalFeesPiconeros))}`,
        label: 'upload fee',
        op: '+',
        // fee-button accumulator totals in legacy sats (1 sat = 1000 piconeros)
        modifier: cost => cost + totalFees,
        omit: !totalFees
      }
    })
  }, [merge])

  // extracts S3 keys from text and updates upload fees
  const $refreshUploadFees = useCallback(() => {
    const isMarkdown = isMarkdownMode(editor)
    if (isMarkdown) {
      const text = $getRoot().getTextContent() || ''
      const s3Keys = [...text.matchAll(AWS_S3_URL_REGEXP)].map(m => Number(m[1]))
      updateUploadFees({ variables: { s3Keys } })
        .then(handleUploadFeesData)
        .catch(err => !isAbortError(err) && console.error(err))
    } else {
      const mediaNodes = $nodesOfType(MediaNode)
      const s3Keys = mediaNodes
        .flatMap(node => [...node.getSrc().matchAll(AWS_S3_URL_REGEXP)])
        .map(m => Number(m[1]))
      updateUploadFees({ variables: { s3Keys } })
        .then(handleUploadFeesData)
        .catch(err => !isAbortError(err) && console.error(err))
    }
  }, [updateUploadFees, handleUploadFeesData])

  // debounced version for update listener
  const refreshUploadFeesDebounced = useDebounceCallback(() => {
    editor.getEditorState().read(() => {
      $refreshUploadFees()
    })
  }, 1000, [$refreshUploadFees])

  // update upload fees when the editor state changes
  useEffect(() => {
    return editor.registerUpdateListener(() =>
      refreshUploadFeesDebounced()
    )
  }, [editor, refreshUploadFeesDebounced])

  return { $refreshUploadFees }
}
